import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableToken,
  RichTextContent,
  useCollectionBinding,
  useContentToken,
  useProperty,
  usePropertyAddressUrl,
} from '@vibesites/runtime/react';
import { Car, Footprints, MapPin } from 'lucide-react';
import { btnPrimary, clean, hasValue } from '../utils/content';

interface Poi {
  poi_name?: string | null;
  poi_type?: string | null;
  poi_address?: string | null;
  poi_business_url?: string | null;
  poi_walking_minutes?: string | null;
  poi_driving_minutes?: string | null;
  poi_miles_from_property?: string | null;
}

export function NeighborhoodGuide() {
  const property = useProperty() as unknown as Record<string, unknown>;
  const directionsUrl = usePropertyAddressUrl();
  const heading = useContentToken('neighborhoodguide.heading');
  const placesHeading = useContentToken('neighborhoodguide.places_heading');
  const pois = useCollectionBinding<Poi>('neighborhoodguide.places', { collection: 'neighborhood_pois', defaultLimit: 24 });

  const groups = useMemo(() => {
    const map = new Map<string, Poi[]>();
    for (const p of pois || []) {
      if (!hasValue(p.poi_name)) continue;
      const key = clean(p.poi_type) || 'Nearby';
      map.set(key, [...(map.get(key) || []), p]);
    }
    return [...map.entries()];
  }, [pois]);

  const address = clean(property?.property_full_address);
  const lat = clean(property?.property_address_latitude);
  const lng = clean(property?.property_address_longitude);
  const query = address || (lat && lng ? `${lat},${lng}` : '');
  const mapSrc = query ? `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=14&output=embed` : '';

  return (
    <section className="bg-muted py-14 md:py-20">
      <div className="mx-auto max-w-[1180px] px-6 lg:px-10">
        <EditableToken path="neighborhoodguide.heading" as="h2" className="js-reveal js-heading text-[30px] text-foreground sm:text-[36px] lg:text-[40px]">
          {heading}
        </EditableToken>
        <RichTextContent path="neighborhoodguide.body" as="div" className="js-reveal js-d1 js-copy mt-5 max-w-[860px] text-foreground" />

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div className="js-reveal-left">
            <EditableToken path="neighborhoodguide.places_heading" as="h3" className="js-heading text-[22px] text-secondary">
              {placesHeading}
            </EditableToken>
            <EditableCollectionBinding bindingPath="neighborhoodguide.places" label="Nearby Places">
              {groups.length === 0 ? (
                <p className="js-copy mt-4 text-[color:var(--vibe-color-text-muted)]">Ask our team for their favorite local spots.</p>
              ) : (
                <div className="mt-5 flex flex-col gap-7">
                  {groups.map(([type, items]) => (
                    <div key={type}>
                      <p className="font-heading text-[15px] font-bold uppercase tracking-[0.12em] text-primary">{type}</p>
                      <ul className="mt-3 divide-y divide-border border-y border-border">
                        {items.map((p, i) => (
                          <li key={`${p.poi_name}-${i}`} className="flex items-start justify-between gap-4 py-3 transition-[background-color,padding] duration-300 ease-out hover:bg-background hover:px-3">
                            <div className="min-w-0">
                              {hasValue(p.poi_business_url) ? (
                                <a href={p.poi_business_url} target="_blank" rel="noopener noreferrer" className="font-body text-[17px] font-semibold text-foreground underline-offset-4 hover:underline">
                                  {clean(p.poi_name)}
                                </a>
                              ) : (
                                <span className="font-body text-[17px] font-semibold text-foreground">{clean(p.poi_name)}</span>
                              )}
                              {hasValue(p.poi_address) && <p className="mt-0.5 font-body text-[14px] text-[color:var(--vibe-color-text-muted)]">{p.poi_address}</p>}
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-1 font-body text-[14px] text-[color:var(--vibe-color-text-muted)]">
                              {hasValue(p.poi_walking_minutes) && (
                                <span className="inline-flex items-center gap-1"><Footprints className="h-4 w-4" aria-hidden />{p.poi_walking_minutes} min</span>
                              )}
                              {hasValue(p.poi_driving_minutes) && (
                                <span className="inline-flex items-center gap-1"><Car className="h-4 w-4" aria-hidden />{p.poi_driving_minutes} min</span>
                              )}
                              {!hasValue(p.poi_walking_minutes) && !hasValue(p.poi_driving_minutes) && hasValue(p.poi_miles_from_property) && (
                                <span>{p.poi_miles_from_property} mi</span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </EditableCollectionBinding>
          </div>

          <div className="js-reveal-right js-d1 flex flex-col">
            {mapSrc ? (
              <div className="aspect-[4/3] w-full overflow-hidden bg-background shadow-md lg:aspect-auto lg:min-h-[460px] lg:flex-1">
                <iframe src={mapSrc} title={`Map of ${address || 'the community'}`} className="h-full w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            ) : null}
            {address && (
              <div className="mt-5 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="flex items-start gap-2 font-body text-[17px] text-foreground">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-primary" aria-hidden />
                  {address}
                </p>
                {directionsUrl && (
                  <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className={`${btnPrimary} !min-h-[44px]`}>
                    Get Directions
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodGuide',
  displayName: 'Neighborhood Guide',
  tokens: {
    'neighborhoodguide.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Live at the Center of It All',
      generator: {
        prompt: 'A short uppercase-friendly headline about living at the heart of this specific neighborhood or city.',
        context: 'Top of the neighborhood guide band on the Neighborhood page, above nearby places and a map.',
        length: '4–8 words',
      },
    },
    'neighborhoodguide.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Dining, coffee, shopping, parks, and everyday essentials are all close to home at <span data-type="field-token" data-field="property_name"></span>.</p>',
      generator: {
        prompt: 'A short, hyperlocal overview of what is close by — named local spots, parks, or venues near this address.',
        context: 'Under the neighborhood guide heading, above a list of nearby places and an embedded map.',
        length: '2–4 sentences',
      },
    },
    'neighborhoodguide.places_heading': { label: 'Places Heading', type: 'text', defaultValue: 'Nearby Favorites' },
  },
  collectionBindings: {
    'neighborhoodguide.places': { label: 'Nearby Places', collection: 'neighborhood_pois', defaultLimit: 24 },
  },
};

(NeighborhoodGuide as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodGuide;
